import { useCallback, useState } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * A TRIAGEM CLICA E FAZ (Douglas 29/10/2026): "o sistema tem que clicar e fazer
 * em todas as triagens". O molde único dos botões de TODAS as triagens:
 *
 * - **uma ida ao banco por clique**, todas as linhas juntas, nunca um laço de
 *   linha por linha esperando uma a outra;
 * - **a linha sai (ou desce) assim que o banco confirma**, sem esperar a fila
 *   inteira ser lida de novo. Se o banco recusa, ela não se mexe e o aviso diz
 *   por quê: nada sai e volta;
 * - **só o pedido clicado fica ocupado**; o resto da fila continua livre;
 * - **o Kanban e a Planilha não são relidos a cada clique**: ficam marcados como
 *   desatualizados e se leem quando aparecem (e o TriagemShell os relê ao fechar).
 *
 * Medido em 29/10: mover o pedido leva 73 ms e a justificativa 31 ms no banco; o
 * clique demorava pelas idas em série e pela releitura das listas.
 */

/** Quem está clicando, lido da sessão já aberta neste navegador. O
 *  `auth.getUser()` fazia uma ida ao servidor a cada botão. */
export async function meuUsuarioId(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.user?.id ?? null;
}

type Rpc = (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: { message?: string } | null }>;
const rpc = supabase.rpc.bind(supabase) as unknown as Rpc;

/** Uma função do banco que faz o clique inteiro numa transação; o erro vem com
 *  a frase do banco (o motivo da recusa). */
export async function noBanco<T>(fn: string, args: Record<string, unknown>, falha: string): Promise<T> {
  const { data, error } = await rpc(fn, args);
  if (error) throw new Error(error.message || falha);
  return data as T;
}

/**
 * O Manter de todas as triagens (`fn_triagem_manter`): nota na timeline,
 * registro do movimento, justificativa da fila e `manter_em` das linhas numa
 * transação, tudo ou nada. Só desce a linha que ainda está em `coluna`.
 */
export async function manterNaTriagem(args: {
  pedidoId: string;
  itens: string[];
  coluna: string | null;
  nota?: string | null;
  movimento?: string | null;
  justificativa?: string | null;
  motivo?: string | null;
  acao?: string | null;
}): Promise<{ mantidos: number; itens: string[] }> {
  const { data, error } = await rpc('fn_triagem_manter', {
    p_pedido_id: args.pedidoId,
    p_itens: args.itens,
    p_coluna: args.coluna,
    p_nota: args.nota ?? null,
    p_movimento: args.movimento ?? null,
    p_justificativa: args.justificativa ?? null,
    p_motivo: args.motivo ?? null,
    p_acao: args.acao ?? null,
  });
  if (error) throw new Error(error.message || 'Não foi possível manter na fila.');
  return (data ?? { mantidos: 0, itens: [] }) as { mantidos: number; itens: string[] };
}

/** A justificativa da fila para várias linhas numa ida só (o banco carimba a
 *  coluna de cada uma e recusa a transação inteira se o texto não passar). */
export async function justificarLinhas(args: {
  pedidoId: string;
  itens: string[];
  justificativa: string;
  motivo?: string | null;
  acao?: string | null;
}): Promise<void> {
  if (args.itens.length === 0) return;
  const usuarioId = await meuUsuarioId();
  const { error } = await supabase.from('fila_justificativas').insert(args.itens.map((itemId) => ({
    item_id: itemId, pedido_id: args.pedidoId, coluna: '', justificativa: args.justificativa.trim(),
    usuario_id: usuarioId, motivo_codigo: args.motivo ?? null, acao_codigo: args.acao ?? null,
  })) as never);
  if (error) throw new Error(error.message || 'Não foi possível registrar a justificativa.');
}

export interface AlvoNaFila {
  pedidoId: string;
  /** Quando a fila lista LINHAS (uma por item), quais linhas o clique moveu.
   *  Sem isso, vale o pedido inteiro. */
  itens?: string[];
}

function ehDoAlvo(x: unknown, alvo: AlvoNaFila): boolean {
  if (!x || typeof x !== 'object') return false;
  const r = x as { pedido_id?: unknown; item_id?: unknown };
  if (alvo.itens && typeof r.item_id === 'string') return alvo.itens.includes(r.item_id);
  return r.pedido_id === alvo.pedidoId;
}

function reorganizar(lista: unknown[], alvo: AlvoNaFila, modo: 'sai' | 'desce'): unknown[] {
  if (!lista.some((x) => ehDoAlvo(x, alvo))) return lista;
  const resto = lista.filter((x) => !ehDoAlvo(x, alvo));
  return modo === 'sai' ? resto : [...resto, ...lista.filter((x) => ehDoAlvo(x, alvo))];
}

function refletir(dado: unknown, alvo: AlvoNaFila, modo: 'sai' | 'desce'): unknown {
  if (Array.isArray(dado)) return reorganizar(dado, alvo, modo);
  if (dado && typeof dado === 'object') {
    let mudou = false;
    const novo: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(dado as Record<string, unknown>)) {
      novo[k] = Array.isArray(v) ? reorganizar(v, alvo, modo) : v;
      if (novo[k] !== v) mudou = true;
    }
    return mudou ? novo : dado;
  }
  return dado;
}

/**
 * Depois que o banco confirmou: a linha sai da fila (`sai`) ou vai para o fim
 * (`desce`) na tela na hora, e a fila se confere sozinha em seguida, sem
 * ninguém esperar por ela. `chaves` são as queryKeys da triagem (o primeiro
 * elemento de cada uma).
 */
export async function depoisDoClique(
  qc: QueryClient,
  chaves: string[],
  alvo: AlvoNaFila,
  modo: 'sai' | 'desce',
): Promise<void> {
  await Promise.all(chaves.map((k) => qc.cancelQueries({ queryKey: [k] })));
  for (const k of chaves) {
    qc.setQueriesData({ queryKey: [k] }, (old: unknown) => refletir(old, alvo, modo));
  }
  for (const k of chaves) void qc.invalidateQueries({ queryKey: [k] });
  void qc.invalidateQueries({ queryKey: ['triagens', 'catalogo'] });
  void qc.invalidateQueries({ queryKey: ['timeline-pedido', alvo.pedidoId] });
  void qc.invalidateQueries({ queryKey: ['timeline-pedido-modal'] });
  marcarTelasDeForaDesatualizadas(qc);
}

/** Kanban e Planilha: marcados como desatualizados, sem reler agora. Eles se
 *  leem de novo quando aparecem; a triagem não espera por eles. */
export function marcarTelasDeForaDesatualizadas(qc: QueryClient): void {
  for (const k of ['kanban-optimized', 'kanban', 'planilha-itens', 'v_planilha_mae']) {
    void qc.invalidateQueries({ queryKey: [k], refetchType: 'none' });
  }
}

/** Ao fechar a triagem aberta por cima do Kanban, ele se lê uma vez só, com
 *  tudo o que mudou. */
export function releTelasDeFora(qc: QueryClient): void {
  for (const k of ['kanban-optimized', 'kanban', 'planilha-itens']) {
    void qc.invalidateQueries({ queryKey: [k] });
  }
}

/**
 * Só o pedido clicado fica ocupado: os botões dele param enquanto o banco
 * responde, e os outros pedidos da fila continuam livres.
 */
export function useLinhasOcupadas() {
  const [ocupadas, setOcupadas] = useState<ReadonlySet<string>>(() => new Set());
  const ocupar = useCallback(async <T,>(id: string, fn: () => Promise<T>): Promise<T> => {
    setOcupadas((s) => new Set(s).add(id));
    try {
      return await fn();
    } finally {
      setOcupadas((s) => {
        const n = new Set(s);
        n.delete(id);
        return n;
      });
    }
  }, []);
  const ocupada = useCallback((id: string) => ocupadas.has(id), [ocupadas]);
  return { ocupada, ocupar, algumaOcupada: ocupadas.size > 0 };
}
